import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Sparkles, ArrowRight, CheckCircle, Shield } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { TREATMENTS } from './treatments/TreatmentBase';
import {
  fadeUp, scaleIn, staggerContainer, staggerItem, pageTransition, viewportConfig
} from '../components/AnimationVariants';

export default function Services() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ rotate: [0, 6, 0] }} transition={{ duration: 10, repeat: Infinity }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Sparkles className="w-4 h-4" /> Our Treatments
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Everything Your Smile Needs
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Comprehensive dental treatments delivered with warmth and genuine attention to detail. Select a treatment to learn more.
          </motion.p>
        </div>
      </section>

      {/* Treatments Grid */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {TREATMENTS.map((treatment, i) => {
              const Icon = treatment.icon;
              return (
                <motion.div key={treatment.slug} variants={staggerItem}>
                  <Link to={`/services/${treatment.slug}`}
                    className="card-wood overflow-hidden group block"
                    style={{ textDecoration: 'none' }}>
                    <motion.div
                      whileHover={{ y: -6, boxShadow: `0 20px 48px ${treatment.accent}22` }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <div className="relative h-28 overflow-hidden">
                        <img src={treatment.photo} alt={treatment.title}
                          className="w-full h-full object-cover" loading="lazy" />
                        <div className={`absolute inset-0 bg-gradient-to-r ${treatment.color} opacity-75`} />
                        <div className="absolute inset-0 p-5 flex items-center gap-3">
                          <motion.div
                            className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0"
                            whileHover={{ rotate: 10, scale: 1.1 }}
                            transition={{ type: 'spring', stiffness: 400 }}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </motion.div>
                          <div>
                            <h3 className="font-serif text-white font-semibold text-lg leading-tight">{treatment.title}</h3>
                            <p className="text-white/70 text-xs italic">{treatment.tagline}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-5">
                        <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">{treatment.intro}</p>
                        <div className="grid grid-cols-2 gap-1.5 mb-4">
                          {treatment.features.slice(0, 4).map((f, j) => (
                            <div key={j} className="flex items-center gap-1 text-xs text-gray-500">
                              <CheckCircle className="w-3 h-3 text-forest flex-shrink-0" />{f}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-1 text-gold font-semibold text-sm group-hover:gap-2 transition-all">
                          Learn More <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-12 px-4 bg-white border-t border-cream-deeper">
        <div className="container-max">
          <motion.div className="bg-sage-pale rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6"
            variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <motion.div className="w-16 h-16 rounded-2xl bg-forest flex items-center justify-center flex-shrink-0"
              whileHover={{ rotate: 8, scale: 1.1 }} transition={{ type: 'spring', stiffness: 400 }}>
              <Shield className="w-8 h-8 text-cream" />
            </motion.div>
            <div className="text-center md:text-left">
              <h3 className="font-serif text-forest font-semibold text-xl mb-1">Insurance & Payment Options</h3>
              <p className="text-gray-600 text-sm leading-relaxed">We believe treatment decisions should feel clear from the start. We explain your options, fees and timescales before any treatment begins, with flexible payment plans available for larger treatments.</p>
            </div>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="flex-shrink-0">
              <Link to="/fees-plans" className="btn-primary text-sm">View Fees <ArrowRight className="w-4 h-4" /></Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-forest-dark to-forest text-center">
        <motion.div className="max-w-2xl mx-auto" variants={scaleIn} initial="hidden" whileInView="visible" viewport={viewportConfig}>
          <h2 className="text-3xl font-serif text-white font-semibold mb-4">Not Sure Which Treatment You Need?</h2>
          <p className="text-cream/75 mb-7">Call <a href="tel:+442088665556" className="text-gold hover:underline font-semibold">020 8866 5556</a>, request a call back, or book your appointment online — our team is here to guide you.</p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Link to="/contact" className="btn-primary px-8 py-4 text-base">Book a Consultation <ArrowRight className="w-5 h-5" /></Link>
          </motion.div>
        </motion.div>
      </section>
    </motion.div>
  );
}
